import { Injectable } from '@angular/core';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import sortBy from 'lodash/sortBy';

import { ProfileService } from '../profile/profile.service';
import { createCachedResource } from '../shared/caching.utils';
import { XferStorage } from '../shared/models/xfer.model';
import { CachingService } from '../caching.service';
import { CoreApiService, queryAll, WriteResult } from '../core-api.service';
import { RequestState } from '../shared/enums/request-state.enum';
import { Device } from '../shared/models/device.model';
import { DicomPeer } from '../shared/models/dicom.model';
import { Group, GroupOption } from '../shared/models/group.model';
import { Project } from '../shared/models/project.model';

@Injectable()
export class AdminService extends CachingService {
  private allGroups = createCachedResource(
    this.profileService.siteAdmin$.pipe(
      switchMap(siteAdmin => queryAll(this.coreApi.groups.query, siteAdmin ? { exhaustive: true } : {}))
    )
  )

  private allProjects = createCachedResource(
    this.profileService.siteAdmin$.pipe(
      switchMap(siteAdmin => queryAll(this.coreApi.projects.query, siteAdmin ? { exhaustive: true } : {}))
    )
  )

  readonly allGroupedProjects$: Observable<GroupOption[]> = combineLatest([
    this.allGroups.result$,
    this.allProjects.result$,
  ]).pipe(
    map(([allGroups, allProjects]) => {
      const groupOptions = allGroups.map(group => {
        const projects = allProjects
          .filter(project => project.group === group._id)
          .map(project => ({ ...project, label: project.label || project._id }));
        return { ...group, label: group.label || group._id, projects: sortBy(projects, ({ label }) => label.toLowerCase()) };
      });
      return sortBy(groupOptions, ({ label }) => label.toLowerCase());
    }),
    shareReplay(1),
  )

  private dicomPeers = {
    state: RequestState.Empty,
    subject: new ReplaySubject<DicomPeer[]>(1),
    request: this.coreApi.dimse.peers.get(),
  }
  readonly dicomPeers$ = this.dicomPeers.subject.asObservable()

  private transferStorages = {
    state: RequestState.Empty,
    subject: new ReplaySubject<XferStorage[]>(1),
    request: queryAll(this.coreApi.xfer.storages.query),
  }
  readonly transferStorages$ = this.transferStorages.subject.asObservable()

  constructor(
    private coreApi: CoreApiService,
    private profileService: ProfileService,
  ) {
    super();
  }

  getAllGroups({ refresh = false } = {}): Observable<Group[]> {
    if (refresh) {
      this.allGroups.refresh$.next();
    }
    return this.allGroups.result$.pipe(filter(() => !(refresh && this.allGroups.pending)));
  }

  getAllProjects({ refresh = false } = {}): Observable<Project[]> {
    if (refresh) {
      this.allProjects.refresh$.next();
    }
    return this.allProjects.result$.pipe(filter(() => !(refresh && this.allProjects.pending)));
  }

  getAllGroupedProjects({ refresh = false } = {}): Observable<GroupOption[]> {
    if (refresh) {
      this.allProjects.refresh$.next();
      this.allGroups.refresh$.next();
    }
    return this.allGroupedProjects$;
  }

  addDevice(body: Partial<Device>): Observable<Device> {
    return this.coreApi.devices.post(body);
  }

  updateDevice(deviceId: string, body: {disabled: boolean}): Observable<Device> {
    return this.coreApi.devices.put(deviceId, body);
  }

  getDeviceInfo(deviceId: string): Observable<Device> {
    return this.coreApi.devices.get(deviceId);
  }

  getDicomPeers({ refresh = false } = {}): Observable<DicomPeer[]> {
    return this.getCacheableResource(this.dicomPeers, this.dicomPeers$, { refresh });
  }

  addDicomPeer(body: Partial<DicomPeer>): Observable<DicomPeer> {
    return this.coreApi.dimse.peers.post(body).pipe(tap(() => {
      this.getDicomPeers({ refresh: true });
    }));
  }

  deleteDicomPeer(peerAet): Observable<WriteResult> {
    return this.coreApi.dimse.peers.delete(peerAet).pipe(tap(() => {
      this.getDicomPeers({ refresh: true });
    }));
  }

  getTransferStorages({ refresh = false } = {}): Observable<XferStorage[]> {
    return this.getCacheableResource(this.transferStorages, this.transferStorages$, { refresh });
  }
}
